// src/App.js
import React from "react";
import { useEffect } from "react";
import Footer from "./app/components/footer/Footer";
import Header from "./app/components/header/Header";
import { ReactKeycloakProvider } from "@react-keycloak/web";
import KeycloakClient from "./app/auth/Keycloak.js";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { ApolloProvider } from "@apollo/client";
import Home from "./app/pages/home/Home";
import TravelResult from "./app/pages/travelResult/TravelResult";
import Notification from "./app/pages/notification/Notification";
import ProfilePage from "./app/pages/profilePage/ProfilePage.js";
import Details from "./app/pages/details/Details";
import PrivateRoute from "./common/privateRoute/PrivateRoute.js";
import MyTripsPage from "./app/pages/myTripsPage/MyTripsPage.js";
import ConfirmationPage from "./app/pages/confirmation/ConfirmationPage.js";
import { LegalPage, FaqPage } from "./app/pages/legal/LegalPage.js";
import { CGU, MENTIONS, CONFIDENTIALITE, FAQ } from "./app/pages/legal/legalContent.js";
import Error from "./app/pages/error/Error.js";
import Preview from "./app/pages/preview/Preview";
import Payments from "./app/pages/payments/Payments";
import { client } from "./config/apolloClient.js";
import { ZorniProvider, setAuthTokens } from "./app/hooks/useZorni.js";
import { PersonProvider } from "./app/hooks/user/usePerson.js";
import { HeaderDockProvider } from "./app/providers/HeaderDockContext";
import { NotificationProvider } from "./app/hooks/NotificationContext.js";
import { OperationProvider } from "./dashboard/hooks/useOperation.js";
import GlobalToast from "./dashboard/common/toast/Toast.js";
import Favorites from "./app/components/trips/favorites/Favorites.js";
import Messenger from "./app/components/event/messenger/Messenger.js";
import PastTripReview from "./app/pages/pastTripReview/PastTripReview.js";
import ReservationPreviewPage from "./app/pages/reservationPreview/ReservationPreviewPage.js";
import { SearchProvider } from "./app/hooks/SearchContext.js";
import { AppDashboard } from "./dashboard/AppDashboard.js";
import MobileBottomNav from "./app/components/mobileBottomNav/MobileBottomNav.js";
import MobileSearchSheet from "./app/components/searshForms/mobile/mobileSearchSheet/MobileSearchSheet.js";
import { TrackingProvider } from "./tracking";

function LegacySearchRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/trips${search}`} replace />;
}

function ScrollToTopOnRouteChange() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function AppContent() {
  const location = useLocation();
  const isAdminRoute = (location.pathname || "").startsWith("/admin");

  return (
    <>
      <ScrollToTopOnRouteChange />
      {!isAdminRoute && <Header />}
      {!isAdminRoute && <MobileBottomNav />}
      {/* Feuille de recherche mobile, ouverte depuis les récapitulatifs */}
      {!isAdminRoute && <MobileSearchSheet />}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/trips" element={<TravelResult />} />
        {/* Ancienne page de recherche mobile : remplacée par la feuille de
            recherche. Les anciens liens arrivent sur les résultats. */}
        <Route path="/trips/search" element={<LegacySearchRedirect />} />
        <Route
          path="/notifications"
          element={<Notification />}
        />
        <Route
          path="/profil"
          element={<PrivateRoute element={<ProfilePage />} />}
        />
        <Route
          path="/my-trips"
          element={<PrivateRoute element={<MyTripsPage />} />}
        />
        <Route
          path="/trips/:tripPublicationId/:tripDate"
          element={<Details />}
        />
        <Route
          path="/trips/:tripPublicationId/:tripDate/booking"
          element={<Preview />}
        />
        <Route
          path="/trips/:tripPublicationId/:tripDate/booking/payments"
          element={<Payments />}
        />
        {/* Publique comme le reste du tunnel : les étapes voyage, informations
            et paiement ne demandent pas de compte non plus. */}
        <Route
          path="/trips/:tripPublicationId/:tripDate/booking/confirmation"
          element={<ConfirmationPage />}
        />
        <Route
          path="/reservation/:reservationId"
          element={<PrivateRoute element={<ReservationPreviewPage />} />}
        />
        <Route
          path="/:reservationId/review"
          element={<PrivateRoute element={<PastTripReview />} />}
        />
        <Route
          path="/favorites"
          element={<PrivateRoute element={<Favorites />} />}
        />
        <Route
          path="/messenger"
          element={<PrivateRoute element={<Messenger />} />}
        />
        <Route
          path="/messenger/:conversationId"
          element={<PrivateRoute element={<Messenger />} />}
        />
        <Route path="/admin/*" element={<AppDashboard />} />
        {/* Pages annexes — contenu provisoire, voir legalContent.js */}
        <Route path="/cgu" element={<LegalPage content={CGU} />} />
        <Route
          path="/mentions-legales"
          element={<LegalPage content={MENTIONS} />}
        />
        <Route
          path="/confidentialite"
          element={<LegalPage content={CONFIDENTIALITE} />}
        />
        <Route path="/faq" element={<FaqPage content={FAQ} />} />
        <Route path="/error" element={<Error />} />
      </Routes>
      {!isAdminRoute && <Footer />}
    </>
  );
}

const App = () => {
  return (
    <div id="app">
      <ReactKeycloakProvider
        authClient={KeycloakClient}
        onTokens={({ token, refreshToken }) => {
          setAuthTokens({ token, refreshToken });
        }}
      >
        <ApolloProvider client={client}>
          <ZorniProvider>
            <OperationProvider>
              <PersonProvider>
                <NotificationProvider>
                  <HeaderDockProvider>
                    <SearchProvider>
                      <TrackingProvider>
                        <BrowserRouter>
                          <AppContent />
                        </BrowserRouter>
                      </TrackingProvider>
                    </SearchProvider>
                  </HeaderDockProvider>
                </NotificationProvider>
              </PersonProvider>
              <GlobalToast />
            </OperationProvider>
          </ZorniProvider>
        </ApolloProvider>
      </ReactKeycloakProvider>
    </div>
  );
};

export default App;
